import { z } from "zod";
import { PDF_TO_SQLITE_DEMO_QUERIES } from "./pdf-to-sqlite-demo";

type ToolResult = {
  content: { type: "text"; text: string }[];
  isError?: boolean;
};
export type PdfParseBrowserTool = {
  name: string;
  title: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations: {
    readOnlyHint: boolean;
    consequentialHint: boolean;
    untrustedContentHint: boolean;
  };
  execute: (
    input: unknown,
    context?: { signal?: AbortSignal },
  ) => Promise<ToolResult>;
};

// Local structural types keep the experimental API isolated from global DOM types.
export type PdfParseModelContext = {
  registerTool: (
    tool: PdfParseBrowserTool,
    options?: { signal: AbortSignal },
  ) => void | Promise<void>;
  unregisterTool?: (name: string) => void;
};

export function findModelContext(
  documentSurface: unknown,
  navigatorSurface: unknown,
): PdfParseModelContext | undefined {
  for (const surface of [documentSurface, navigatorSurface]) {
    const context = (
      surface as { modelContext?: PdfParseModelContext } | undefined
    )?.modelContext;
    if (typeof context?.registerTool === "function") return context;
  }
}

export const publicWorkflows = {
  "pdf-to-csv": "/convert/pdf-to-csv",
  "pdf-to-json": "/pdf-to-json",
  "pdf-to-sqlite": "/pdf-to-sqlite",
  "invoice-demo": "/demo/invoices",
  "bank-statement-demo": "/demo/bank-statements",
  "receipt-demo": "/demo/receipts",
  "purchase-order-demo": "/demo/purchase-orders",
  "mcp-setup": "/docs/mcp",
  "api-setup": "/docs/getting-started",
} as const;

function result(value: unknown, isError = false): ToolResult {
  return {
    content: [
      {
        type: "text",
        text: typeof value === "string" ? value : JSON.stringify(value),
      },
    ],
    ...(isError && { isError }),
  };
}

export function createPdfParseBrowserTools({
  fetch: fetchResource,
  navigate,
}: {
  fetch: typeof globalThis.fetch;
  navigate: (path: string) => void;
}): PdfParseBrowserTool[] {
  async function request(
    path: string,
    signal?: AbortSignal,
    init?: RequestInit,
  ) {
    const response = await fetchResource(path, {
      ...init,
      signal,
      credentials: "omit",
    });
    if (!response.ok)
      throw new Error(`Public resource unavailable (HTTP ${response.status}).`);
    return response;
  }

  function tool<T extends z.ZodObject>(
    name: string,
    title: string,
    description: string,
    schema: T,
    run: (input: z.infer<T>, signal?: AbortSignal) => Promise<unknown>,
    readOnly = true,
    untrusted = false,
  ): PdfParseBrowserTool {
    return {
      name,
      title,
      description,
      inputSchema: z.toJSONSchema(schema),
      annotations: {
        readOnlyHint: readOnly,
        consequentialHint: false,
        untrustedContentHint: untrusted,
      },
      async execute(input, context) {
        const parsed = schema.safeParse(input);
        if (!parsed.success)
          return result(
            {
              error: "Invalid tool arguments.",
              issues: parsed.error.issues.map(({ path, message }) => ({
                path,
                message,
              })),
            },
            true,
          );
        try {
          context?.signal?.throwIfAborted();
          return result(await run(parsed.data, context?.signal));
        } catch (error) {
          if (context?.signal?.aborted) throw error;
          return result(
            {
              error:
                error instanceof Error
                  ? error.message
                  : "The public resource could not be loaded.",
            },
            true,
          );
        }
      },
    };
  }

  return [
    tool(
      "search_pdfparse_docs",
      "Search PdfParse documentation",
      "Search public PdfParse guides and REST API references for document extraction, authentication, MCP, routing, and integrations. Returns up to 8 matching entries from the documentation index; no private project data.",
      z
        .object({
          query: z
            .string()
            .trim()
            .min(2)
            .max(200)
            .describe(
              "Documentation question or keywords, for example upload PDF or MCP OAuth.",
            ),
        })
        .strict(),
      async ({ query }, signal) => {
        const data = await (
          await request(
            `/api/search?query=${encodeURIComponent(query)}`,
            signal,
          )
        ).json();
        return {
          results: Array.isArray(data) ? data.slice(0, 8) : data,
          documentation: "https://pdfparse.net/docs",
        };
      },
    ),
    tool(
      "read_pdfparse_guide",
      "Read a PdfParse guide",
      "Read a public PdfParse guide as clean Markdown. Use an empty slug for the overview, getting-started for API setup, how-to-extract-pdf-data for upload and extraction, or mcp, mcp/examples, mcp/actions, webmcp. Generated REST endpoint pages are available through search_pdfparse_docs and /openapi.json instead.",
      z
        .object({
          slug: z
            .string()
            .max(160)
            .regex(/^(?:[a-z0-9-]+(?:\/[a-z0-9-]+)*)?$/)
            .describe(
              "Guide slug without /docs/, leading slash, or file extension. Empty string means overview.",
            ),
        })
        .strict(),
      async ({ slug }, signal) =>
        (await request(`/llms/docs${slug ? `/${slug}` : ""}`, signal)).text(),
    ),
    tool(
      "inspect_pdfparse_sqlite_demo",
      "Inspect the public PDF to SQLite demo",
      "Get the public sample database schema and the five supported read-only query IDs and SQL statements. These are sample shipping invoices and tracking events, not the user's documents. No sign-in or credits required.",
      z.object({}).strict(),
      async (_input, signal) => ({
        sampleData: true,
        schema: await (await request("/api/pdf-to-sqlite-demo", signal)).json(),
        queries: PDF_TO_SQLITE_DEMO_QUERIES.map(({ id, label, sql }) => ({
          id,
          label,
          sql,
        })),
      }),
      true,
      true,
    ),
    tool(
      "query_pdfparse_sqlite_demo",
      "Query the public PDF to SQLite demo",
      "Run one curated read-only query over public sample shipping invoices and tracking events. Returns rows, columns, duration, and rows read. Only the listed query IDs are supported; arbitrary SQL and private project data are unavailable. No sign-in or processing credits required.",
      z
        .object({
          queryId: z
            .enum(PDF_TO_SQLITE_DEMO_QUERIES.map((query) => query.id))
            .describe("Curated query ID from inspect_pdfparse_sqlite_demo."),
        })
        .strict(),
      async ({ queryId }, signal) => {
        const data: unknown = await (
          await request("/api/pdf-to-sqlite-demo", signal, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ queryId }),
          })
        ).json();
        if (!data || typeof data !== "object" || Array.isArray(data))
          throw new Error("Invalid public demo response.");
        return { ...data, sampleData: true };
      },
      true,
      true,
    ),
    tool(
      "open_pdfparse_workflow",
      "Open a PdfParse workflow",
      "Navigate the current browser tab to a supported PdfParse converter, document demo, or integration setup guide. Opens the entry page only; the user continues through normal sign-in, verification, upload, and processing steps. Does not upload files, start extraction, or purchase a plan. JSON and SQLite destinations are product pages with demos.",
      z
        .object({
          workflow: z
            .enum(
              Object.keys(publicWorkflows) as [
                keyof typeof publicWorkflows,
                ...(keyof typeof publicWorkflows)[],
              ],
            )
            .describe("The converter, document demo, or setup guide to open."),
        })
        .strict(),
      async ({ workflow }) => {
        const path = publicWorkflows[workflow];
        navigate(path);
        return {
          opened: path,
          nextStep: "Continue in the page using its normal controls.",
        };
      },
      false,
    ),
  ];
}

export function registerPdfParseBrowserTools(
  context: PdfParseModelContext,
  tools: PdfParseBrowserTool[],
) {
  const controller = new AbortController();
  const registered = new Set<string>();
  const usesLegacyCleanup = typeof context.unregisterTool === "function";
  for (const tool of tools) {
    try {
      const registration = usesLegacyCleanup
        ? context.registerTool(tool)
        : context.registerTool(tool, { signal: controller.signal });
      registered.add(tool.name);
      void Promise.resolve(registration).catch(() => {
        registered.delete(tool.name);
        if (!controller.signal.aborted)
          console.warn(`WebMCP registration failed: ${tool.name}`);
      });
    } catch {
      console.warn(`WebMCP registration failed: ${tool.name}`);
    }
  }
  return () => {
    // Current API uses the registration signal; older navigator implementations
    // use unregisterTool. Neither path clears another component's tools.
    controller.abort();
    for (const name of registered) context.unregisterTool?.(name);
  };
}
